import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Pill, Section, Kpi, fmt } from '../components/ui-kit';
import { Activity } from 'lucide-react';

interface Service {
  name: string;
  status: 'up' | 'degraded' | 'down';
  latency_ms: number;
  uptime_pct: number;
}

export default function Monitoring() {
  const { getToken } = useXyqarAuth();
  const [services, setServices] = React.useState<Service[] | null>(null);
  const [lastTick, setLastTick] = React.useState<Date | null>(null);

  React.useEffect(() => {
    const tick = async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ services: Service[] }>('/api/v1/admin/monitoring', t);
        setServices(j.services ?? []);
        setLastTick(new Date());
      } catch {
        setServices([]);
        setLastTick(new Date());
      }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [getToken]);

  const total = services?.length ?? 0;
  const up = services?.filter((s) => s.status === 'up').length ?? 0;
  const degraded = services?.filter((s) => s.status === 'degraded').length ?? 0;
  const down = services?.filter((s) => s.status === 'down').length ?? 0;
  const avgLatency =
    services && services.length
      ? Math.round(services.reduce((acc, s) => acc + s.latency_ms, 0) / services.length)
      : 0;
  const avgUptime =
    services && services.length
      ? services.reduce((acc, s) => acc + s.uptime_pct, 0) / services.length
      : 0;

  return (
    <div>
      <PageTitle
        kicker="Infrastructure"
        title="Monitoring"
        right={
          <div className="flex items-center gap-2 text-[10px] text-white/40 font-mono uppercase">
            <Activity className="h-3 w-3" /> Polling 10s
            {lastTick && <span>· last {lastTick.toLocaleTimeString()}</span>}
          </div>
        }
      />

      <Section title="Posture summary" hint="Live health of every backend service. Polled every 10 s.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi accent="green" label="Up" value={fmt(up)} hint={`of ${total}`} />
          <Kpi accent={degraded > 0 ? 'amber' : undefined} label="Degraded" value={fmt(degraded)} />
          <Kpi accent={down > 0 ? 'red' : undefined} label="Down" value={fmt(down)} />
          <Kpi label="Avg latency" value={`${avgLatency}ms`} hint={`uptime ${avgUptime.toFixed(2)}%`} />
        </div>
      </Section>

      <Section title="Services" hint="Current admin API exposes a small runtime heartbeat. Use this page as an operational pulse, not a full observability console.">
        {services === null && <Card>Loading…</Card>}
        {services && services.length === 0 && (
          <Card>
            <p className="text-white/60 text-sm">No services monitored yet.</p>
            <p className="text-white/40 text-xs mt-1">
              Extend <code className="font-mono">/api/v1/admin/monitoring</code> if you want traces, queue depth, saturation, or error-budget reporting here.
            </p>
          </Card>
        )}
        {services && services.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {services.map((s) => {
              const tone =
                s.status === 'up' ? 'green' : s.status === 'degraded' ? 'amber' : 'red';
              const overTarget = s.latency_ms > 500;
              return (
                <Card key={s.name}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-white">{s.name}</p>
                    <Pill kind={tone}>{s.status}</Pill>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-white/40 uppercase tracking-wider">Latency</p>
                      <p className={`text-lg ${overTarget ? 'text-amber-300' : 'text-white'}`}>
                        {s.latency_ms}ms
                      </p>
                      {overTarget && (
                        <p className="text-[10px] text-amber-300/70 font-mono mt-0.5">
                          exceeds 500 ms target
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-white/40 uppercase tracking-wider">Uptime</p>
                      <p className="text-white text-lg">{s.uptime_pct.toFixed(2)}%</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
